// Branch-isolation / lineage filter helpers.
//
// PURPOSE
//   Each viewer should only see the slice of the shared family graph that is
//   connected to THEM via blood (parent / child / sibling chain) plus the
//   spouses-of-bloodline members that act as visual connectors. This keeps
//   privacy clean (the other parent's extended family doesn't leak across
//   to the viewer) and the tree visually focused.
//
// PURE & ADDITIVE
//   - These helpers NEVER mutate database rows.
//   - They NEVER touch existing relationships, parent_ids, spouse_id,
//     photos, memories, or layout.
//   - They return a filtered VIEW of the same row objects so downstream
//     renderers (FamilyTreeView, RelativeRoster, projectMembersForViewer)
//     can stay unchanged.
//
// THE BLOODLINE RULE
//   For a viewer V, the bloodline visible to V is the smallest set S where:
//     1. V ∈ S
//     2. Every ancestor of V (via parent_ids walk upward) ∈ S
//     3. For every member M whose parent_ids contain at least ONE member
//        already in S, M ∈ S  (transitive closure downward through
//        parent_ids of every bloodline person).
//   This automatically yields:
//     - V's siblings (share a parent that is V's parent ∈ S)
//     - V's children, grandchildren, …  (parent ∈ S)
//     - V's aunts/uncles  (share a parent with V's parent — that shared
//       parent is V's grandparent ∈ S)
//     - V's cousins, second cousins, niblings — every descendant of any
//       sibling-of-an-ancestor.
//   And automatically EXCLUDES:
//     - The other parent's parents/aunts/cousins (they are not V's
//       ancestors, and their parent_ids don't intersect V's bloodline).
//     - Spouse's family beyond the spouse themselves.
//     - Disconnected rows that some other family member added without
//       linking parent_ids back to V's lineage.
//
// SPOUSE CONNECTORS
//   A spouse of any bloodline member is added to the visible set as a
//   "connector" (they appear next to their bloodline partner so couples
//   render together and shared children are anchored). Their OWN family
//   beyond them is NOT pulled in — only the spouse row itself.
//
// FALLBACK
//   When viewerId is null we cannot compute a bloodline — callers should
//   show the whole graph (legacy behaviour). The default export of
//   `filterMembersToLineage` makes this explicit by returning the input
//   list unchanged in that case.

import type { GraphMember } from '@/contexts/FamilyGraphContext';

/**
 * Hard upper bound on any single traversal in this module. Real family
 * graphs are vanishingly unlikely to reach this depth (200 generations
 * = ~5,000 years of ancestry), so hitting it always indicates a cycle
 * or malformed parent_ids. We log a single warning per traversal and
 * abort safely so render never crashes with "Maximum call stack size
 * exceeded". Pure defensive guard — never mutates any row.
 */
const MAX_TRAVERSAL_DEPTH = 200;
/**
 * Secondary safety bound on iterative WORKLIST length (BFS queue size +
 * total visits). Capped at a few orders of magnitude above MAX_TRAVERSAL_DEPTH
 * so even pathological wide-but-shallow graphs cannot loop forever.
 */
const MAX_TRAVERSAL_VISITS = 50_000;

/** Emit a single cycle/overflow warning per call site (best-effort). */
const warnCycle = (where: string, detail: Record<string, unknown>): void => {
  try {
    // eslint-disable-next-line no-console
    console.warn(`[KinsCircle][lineage] cycle / overflow protection tripped in ${where}`, detail);
  } catch {
    /* never re-throw from a safety warning */
  }
};

export interface LineageResult {
  /** Member ids that are bloodline-connected to the viewer (incl. viewer). */
  bloodlineIds: Set<string>;
  /**
   * Member ids that are visible-as-connectors (spouses of bloodline members).
   * Disjoint from bloodlineIds. Renderers may want to style these
   * differently (e.g. lighter card, no edit affordance for V).
   */
  spouseConnectorIds: Set<string>;
  /** Union of bloodlineIds + spouseConnectorIds — the full visible set. */
  visibleIds: Set<string>;
}

/**
 * Walk parent_ids upward from viewer and include every ancestor.
 *
 * CYCLE-SAFE: maintains a visited Set so any malformed parent_ids loop
 * (A → B → A, self-reference, diamond shortcut) terminates after each
 * id is processed at most once. Also caps total visits as a secondary
 * safety bound.
 */
const collectAncestors = (
  viewerId: string,
  byId: Map<string, GraphMember>,
): Set<string> => {
  const out = new Set<string>([viewerId]);
  const queue: string[] = [viewerId];
  let visits = 0;
  while (queue.length) {
    if (visits++ > MAX_TRAVERSAL_VISITS) {
      warnCycle('collectAncestors', { viewerId, visited: out.size });
      break;
    }
    const cur = queue.shift()!;
    const m = byId.get(cur);
    if (!m) continue;
    for (const pid of m.parentIds ?? []) {
      if (!pid) continue;
      if (out.has(pid)) continue; // already visited — cycle-safe
      if (!byId.has(pid)) continue;
      out.add(pid);
      queue.push(pid);
    }
  }
  return out;
};


/**
 * Compute the bloodline + spouse-connector sets for a given viewer.
 *
 * Pure function — no side effects.
 */
export const computeLineage = (
  members: GraphMember[],
  viewerId: string | null,
): LineageResult => {
  if (!viewerId) {
    const all = new Set(members.map((m) => m.id));
    return {
      bloodlineIds: all,
      spouseConnectorIds: new Set(),
      visibleIds: all,
    };
  }
  const byId = new Map(members.map((m) => [m.id, m]));
  if (!byId.has(viewerId)) {
    const all = new Set(members.map((m) => m.id));
    return {
      bloodlineIds: all,
      spouseConnectorIds: new Set(),
      visibleIds: all,
    };
  }

  // Step 1 + 2 — viewer plus all ancestors.
  const bloodline = collectAncestors(viewerId, byId);

  // Step 3 — transitive closure downward: any member whose parent_ids
  // intersect the current bloodline set is added. Repeat until stable.
  // We process iteratively because the set grows; using a worklist keeps
  // this O(N * average-parents-per-row).
  //
  // CYCLE-SAFE: the outer fixed-point loop is bounded by `members.length + 1`
  // because each iteration that sets `changed=true` adds at least one new
  // member id to the bloodline set, so we cannot exceed N iterations. The
  // hard cap below is a defensive belt-and-suspenders bound — if a future
  // refactor accidentally re-adds a member, we still terminate.
  let changed = true;
  let iter = 0;
  const maxIter = members.length + 2;
  while (changed) {
    if (iter++ > maxIter) {
      warnCycle('computeLineage.downwardClosure', {
        viewerId,
        bloodlineSize: bloodline.size,
        iter,
      });
      break;
    }
    changed = false;
    for (const m of members) {
      if (bloodline.has(m.id)) continue;
      const parents = m.parentIds ?? [];
      if (parents.length === 0) continue;
      for (const pid of parents) {
        if (pid && bloodline.has(pid)) {
          bloodline.add(m.id);
          changed = true;
          break;
        }
      }
    }
  }


  // Spouse connectors — spouses of any bloodline member are added as
  // visible connectors (so couples render together) but their own
  // ancestors / siblings / etc. are NOT pulled in.
  //
  // SPOUSE VISIBILITY FIX (May 2026 — Chekesha's husband Terrance
  // missing from her viewer-centered tree):
  //   Previously this loop only walked the FORWARD direction —
  //   bloodline member's `spouseId` pointing at the partner. When the
  //   spouse_id was only stored on the OTHER side (one-sided: partner's
  //   `spouseId` points at the bloodline member but the bloodline
  //   member's own `spouseId` is null), the partner was silently
  //   dropped from `visibleIds`, then filtered out by
  //   `filterMembersToLineage`, then never reached pyramidLayout, so
  //   pyramidLayout's spouse-anchored tier inheritance had nothing to
  //   anchor and the heart connector / couple unit never rendered.
  //
  //   The fix walks BOTH directions: any member whose `spouseId`
  //   points AT a bloodline member is also a spouse connector.
  //   Reciprocity is no longer required — one direction of spouse_id
  //   is enough to surface the partner in the visible set. This
  //   matches the rule pyramidLayout's `buildCouples` already uses
  //   for the heart-line couple unit (forward OR reverse spouse_id
  //   is enough to form a couple).
  //
  //   Pure presentation filter — never mutates spouse_id, never
  //   touches parent_ids, never duplicates the partner, never moves
  //   them to a cousin/sibling/parent row. They simply become
  //   eligible to render beside the centered viewer.
  const spouseConnectors = new Set<string>();
  // Pass A — forward direction: bloodline.spouseId → partner.
  for (const id of bloodline) {
    const m = byId.get(id);
    if (!m?.spouseId) continue;
    if (bloodline.has(m.spouseId)) continue; // spouse already in bloodline
    if (!byId.has(m.spouseId)) continue;
    spouseConnectors.add(m.spouseId);
  }
  // Pass B — reverse direction: any candidate.spouseId → bloodline.id.
  // This is the path Chekesha needed: Terrance's row stores
  // spouseId=Chekesha but Chekesha's own row doesn't store
  // spouseId=Terrance. Pass A misses him; Pass B picks him up.
  for (const m of members) {
    if (!m.spouseId) continue;
    if (m.id === viewerId) continue;
    if (bloodline.has(m.id)) continue;        // already in bloodline
    if (spouseConnectors.has(m.id)) continue; // already added by Pass A
    if (!bloodline.has(m.spouseId)) continue; // not pointing at bloodline
    spouseConnectors.add(m.id);
  }
  const visible = new Set<string>([...bloodline, ...spouseConnectors]);

  return {
    bloodlineIds: bloodline,
    spouseConnectorIds: spouseConnectors,
    visibleIds: visible,
  };
};


/**
 * Filter a member list down to just the rows visible to the given viewer
 * via the bloodline rule + spouse connectors. Returns the input array
 * unchanged when viewerId is null (legacy whole-graph view).
 *
 * IMPORTANT: this filter never modifies any row, never deletes anything
 * from the database, and never alters parent_ids/spouse_id/relationships.
 * It is a pure presentation filter — the underlying data is preserved
 * in full.
 *
 * REGRESSION REPAIR (Nov 2026 — "I added a sister/cousin/grandfather
 * and they vanished from All Relatives"):
 *   The bloodline walk only traverses parent_ids edges. A newly-added
 *   relative whose parent_ids is empty (because the user saved without
 *   picking a connector — which is now allowed per spec) had no edge
 *   to walk and was therefore filtered out of the visible set. The
 *   row was saved successfully in the DB, but disappeared from the
 *   roster + tree, producing the "fails silently" symptom.
 *
 *   The fix: we now also include any "floating" member — a row whose
 *   parent_ids is empty AND who is not listed as a parent on anyone
 *   else's parent_ids — in the visible set. Floating rows surface in
 *   the "Needs connection" overflow tier so the user can see they were
 *   saved AND link them later. Bloodline isolation for OTHER side's
 *   extended family is unchanged: those rows are non-floating (they
 *   have parent_ids that point at the other side) and stay filtered.
 */
export const filterMembersToLineage = (
  members: GraphMember[],
  viewerId: string | null,
): GraphMember[] => {
  if (!viewerId) return members;
  const { visibleIds, bloodlineIds } = computeLineage(members, viewerId);
  // Safeguard: if the viewer has NO parent_ids filled in (legacy data),
  // the bloodline walk will only contain the viewer themselves — which
  // would render as an empty tree. Fall back to the full graph in that
  // case so we never accidentally hide everyone behind a missing link.
  // The user can still link parents later to enable real branch isolation.
  if (bloodlineIds.size <= 1) return members;
  if (visibleIds.size === 0) return members;

  // Build the "is listed as a parent on anyone" set so we can detect
  // truly floating rows (no parents AND no descendants).
  const referencedAsParent = new Set<string>();
  for (const m of members) {
    for (const pid of m.parentIds ?? []) {
      if (pid) referencedAsParent.add(pid);
    }
  }

  return members.filter((m) => {
    if (visibleIds.has(m.id)) return true;
    // FLOATING-ROW PASSTHROUGH — keep newly-added unconnected relatives
    // visible so the user sees the row they just saved. They show up in
    // the roster's "Needs connection" tier and can be linked from there.
    const noParents = (m.parentIds ?? []).filter(Boolean).length === 0;
    const noDescendants = !referencedAsParent.has(m.id);
    if (noParents && noDescendants) return true;
    return false;
  });
};


// ────────────────────────────────────────────────────────────────────────
// BRANCH-SCOPE FILTER (Nov 2026 — global branch isolation)
// ────────────────────────────────────────────────────────────────────────
//
// Purpose
//   When a CLAIMED family member adds relatives that don't belong to the
//   graph owner's bloodline (e.g. their in-laws, their other-parent-side
//   ancestors / cousins, branch-only relatives), those rows are tagged in
//   the DB with `metadata.branch_owner_member_id = <claimed-member-id>`.
//   This filter hides such rows from the CENTERED viewer's tree unless
//   the viewer is THAT branch owner OR a descendant of them.
//
// Rules
//   • Rows with no `branchOwnerMemberId` are ALWAYS visible (legacy
//     behaviour for every pre-existing row — nothing is hidden by
//     default).
//   • Rows where `branchOwnerMemberId === centeredViewerId` are visible.
//   • Rows where the centered viewer is a DESCENDANT of the branch owner
//     (via parent_ids closure) are visible — i.e. branch-scoped rows
//     bubble down through that branch owner's own descendants.
//   • Otherwise the row is HIDDEN from this centered view. The row is
//     never mutated or deleted; it remains in the DB and will appear when
//     the centered viewer switches to the branch owner (or any of their
//     descendants).
//
// Pure presentation filter — never writes to the database, never modifies
// parent_ids / spouse_id / kinship / relationships / claims. Existing
// rows that pre-date the branch-scope tag continue to render exactly as
// before because their `branchOwnerMemberId` is null.

/**
 * Hide branch-scoped rows that don't belong to the centered viewer's
 * branch. See module-level notes above for the exact rule.
 */
export const filterMembersByBranchScope = (
  members: GraphMember[],
  centeredViewerId: string | null,
): GraphMember[] => {
  if (!centeredViewerId) return members;
  if (!members.some((m) => m.branchOwnerMemberId)) return members;
  const byId = new Map(members.map((m) => [m.id, m]));
  // Memoize "is centeredViewerId a descendant of branchOwnerId?" since the
  // same branch owner is typically shared by many rows.
  const ancestryCache = new Map<string, boolean>();
  const viewerIsDescendantOf = (branchOwnerId: string): boolean => {
    if (branchOwnerId === centeredViewerId) return true;
    if (ancestryCache.has(branchOwnerId)) return ancestryCache.get(branchOwnerId)!;
    // Walk UP from the centered viewer; if we hit the branch owner, the
    // viewer descends from them.
    //
    // CYCLE-SAFE: `seen` Set ensures every id is enqueued at most once.
    // The visit-count cap is a defensive secondary bound — if a future
    // change ever broke the seen-set guard, we still abort cleanly
    // instead of blowing the call stack. Returning `false` on overflow
    // is the SAFE default (treat as "not a descendant" → hides branch-
    // scoped rows from this viewer — never crashes render).
    const seen = new Set<string>([centeredViewerId]);
    const queue: string[] = [centeredViewerId];
    let found = false;
    let visits = 0;
    while (queue.length) {
      if (visits++ > MAX_TRAVERSAL_VISITS) {
        warnCycle('filterMembersByBranchScope.viewerIsDescendantOf', {
          centeredViewerId,
          branchOwnerId,
          visited: seen.size,
        });
        break;
      }
      const cur = queue.shift()!;
      if (cur === branchOwnerId) {
        found = true;
        break;
      }
      const m = byId.get(cur);
      if (!m) continue;
      for (const pid of m.parentIds ?? []) {
        if (!pid || seen.has(pid)) continue;
        seen.add(pid);
        queue.push(pid);
      }
    }
    ancestryCache.set(branchOwnerId, found);
    return found;
  };
  return members.filter((m) => {
    const branchOwnerId = m.branchOwnerMemberId ?? null;
    if (!branchOwnerId) return true; // Unrestricted row — always visible.
    if (branchOwnerId === m.id) return true; // Self-tagged row — safety pass-through.
    return viewerIsDescendantOf(branchOwnerId);
  });
};



/**
 * Branch-side classification used by the visual tree to keep mother-side
 * and father-side branches clearly separated. Pure helper — does not
 * read or write the database. Callers can pass the result to existing
 * side-aware renderers without changing any storage.
 *
 *   - 'mother'   → the member descends from the viewer's mother (the
 *                  shared-parent walk leads through a parent the viewer
 *                  has tagged as 'mother' OR the first parent slot when
 *                  no explicit tagging exists).
 *   - 'father'   → mirror image for the father side.
 *   - 'self'     → on the viewer's own descent line (children,
 *                  grandchildren, niblings, etc. — they belong to the
 *                  viewer's own branch, not to either parent's).
 *   - 'both'     → connectable from both sides (rare; e.g. a relative
 *                  who descends from a shared great-grandparent).
 */
export type BranchSide = 'mother' | 'father' | 'self' | 'both';

/**
 * Resolve which "side" branch each visible member belongs to from the
 * viewer's perspective. Returns Map<memberId, BranchSide>. Members not
 * in the visible lineage simply aren't in the map.
 *
 * Used by the tree renderer to KEEP mother-side and father-side branches
 * visually separated and to avoid merging unrelated sub-trees.
 */
export const resolveBranchSides = (
  members: GraphMember[],
  viewerId: string | null,
): Map<string, BranchSide> => {
  const out = new Map<string, BranchSide>();
  if (!viewerId) return out;
  const byId = new Map(members.map((m) => [m.id, m]));
  const viewer = byId.get(viewerId);
  if (!viewer) return out;

  out.set(viewerId, 'self');

  // Identify viewer's two "side roots" — the parents that anchor each
  // half of the ancestry. Honor stored mother/father labels first.
  const viewerParentIds = (viewer.parentIds ?? []).filter((pid) => byId.has(pid));
  let motherRoot: string | null =
    viewerParentIds.find((pid) => byId.get(pid)?.relationship === 'mother') ??
    null;
  let fatherRoot: string | null =
    viewerParentIds.find((pid) => byId.get(pid)?.relationship === 'father') ??
    null;
  if (!motherRoot && !fatherRoot && viewerParentIds.length > 0) {
    motherRoot = viewerParentIds[0] ?? null;
    fatherRoot = viewerParentIds[1] ?? null;
  } else if (!motherRoot && viewerParentIds.length > 1) {
    motherRoot = viewerParentIds.find((pid) => pid !== fatherRoot) ?? null;
  } else if (!fatherRoot && viewerParentIds.length > 1) {
    fatherRoot = viewerParentIds.find((pid) => pid !== motherRoot) ?? null;
  }

  // BFS down each side root through the parent → child relationship to
  // tag every descendant of that side. We walk parent_ids in REVERSE
  // (i.e. find members whose parent_ids contain the current frontier).
  const childrenOf = new Map<string, string[]>();
  for (const m of members) {
    for (const pid of m.parentIds ?? []) {
      if (!pid) continue;
      if (!childrenOf.has(pid)) childrenOf.set(pid, []);
      childrenOf.get(pid)!.push(m.id);
    }
  }

  const tagDescent = (rootId: string, side: BranchSide) => {
    // Walk UP from rootId to all of rootId's ancestors first (so e.g.
    // mother's parents and grandparents are tagged 'mother' too).
    const ancestors = new Set<string>([rootId]);
    const upQueue: string[] = [rootId];
    while (upQueue.length) {
      const cur = upQueue.shift()!;
      const m = byId.get(cur);
      if (!m) continue;
      for (const pid of m.parentIds ?? []) {
        if (!pid || !byId.has(pid)) continue;
        if (ancestors.has(pid)) continue;
        ancestors.add(pid);
        upQueue.push(pid);
      }
    }
    for (const aid of ancestors) {
      const prev = out.get(aid);
      if (prev && prev !== side) out.set(aid, 'both');
      else out.set(aid, side);
    }
    // Then walk DOWN from each ancestor through children, but stop when
    // we hit the viewer (their descent is 'self', not the parent's side).
    for (const aid of ancestors) {
      const downQueue: string[] = [aid];
      const seen = new Set<string>([aid]);
      while (downQueue.length) {
        const cur = downQueue.shift()!;
        const kids = childrenOf.get(cur) ?? [];
        for (const cid of kids) {
          if (cid === viewerId) continue; // viewer is 'self'
          if (seen.has(cid)) continue;
          seen.add(cid);
          const prev = out.get(cid);
          if (prev && prev !== side && prev !== 'self') out.set(cid, 'both');
          else if (prev !== 'self') out.set(cid, side);
          downQueue.push(cid);
        }
      }
    }
  };

  if (motherRoot) tagDescent(motherRoot, 'mother');
  if (fatherRoot) tagDescent(fatherRoot, 'father');

  // Viewer's own descendants → 'self'.
  const downQueue: string[] = [viewerId];
  const seenSelf = new Set<string>([viewerId]);
  while (downQueue.length) {
    const cur = downQueue.shift()!;
    const kids = childrenOf.get(cur) ?? [];
    for (const cid of kids) {
      if (seenSelf.has(cid)) continue;
      seenSelf.add(cid);
      out.set(cid, 'self');
      downQueue.push(cid);
    }
  }

  return out;
};

/**
 * Permission helper — does the viewer have direct-lineage authority over
 * the given member? "Direct lineage" per the privacy spec is:
 *   - their own profile (member.id === viewerId)
 *   - profiles they created  (member.createdBy === userId)
 *   - their parents, children, siblings (one structural hop away)
 *
 * Used by ClaimPerms / UI gating to decide whether to surface
 * relationship-edit affordances on a given card. The graph owner /
 * staff override still applies on top of this — it's an ADDITIONAL
 * permission grant, never a restriction on existing admin capabilities.
 */
export const isDirectLineage = (
  member: GraphMember,
  viewerId: string | null,
  members: GraphMember[],
): boolean => {
  if (!viewerId) return false;
  if (member.id === viewerId) return true;
  const byId = new Map(members.map((m) => [m.id, m]));
  const viewer = byId.get(viewerId);
  if (!viewer) return false;
  const viewerParentIds = new Set((viewer.parentIds ?? []).filter(Boolean));
  // Parent of viewer.
  if (viewerParentIds.has(member.id)) return true;
  // Child of viewer.
  if ((member.parentIds ?? []).includes(viewerId)) return true;
  // Sibling of viewer — share at least one parent with viewer.
  if (viewerParentIds.size > 0) {
    for (const pid of member.parentIds ?? []) {
      if (pid && viewerParentIds.has(pid)) return true;
    }
  }
  // Spouse of viewer.
  if (viewer.spouseId && viewer.spouseId === member.id) return true;
  return false;
};
